import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import type { RoleCode } from '../../api/contrat.js';
import { MENTION_ESTIMATION } from '../../../../../packages/contracts/src/entities/index.js';
import { cheminEcran, libelleNavigation, peutOuvrir } from '../../app/ecrans.js';
import { Lien } from '../../app/navigation.js';
import { LARGEUR_BUREAU } from '../../app/roles.js';
import { ongletsDossier } from '../../app/onglets-dossier.js';
import { CIBLE_MIN, COULEURS, POLICE_EQUIPE, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import type { CatalogueDemarrage, EtapeCatalogue, Intervenant, OptionQuestion } from './catalogue.js';
import { AvertissementForcage } from './forcage.js';
import {
  dateDeVersion,
  etapes,
  euros,
  jourMois,
  CONDITION_MANDAT_RECHERCHE,
  evaluerConditions,
  manquantes,
  optionChoisie,
  parCode,
  resumer,
  type AvancementEtape,
  type FaitsDossier,
  type ForcageLu,
  type ResumePhase,
  type Statut,
} from './modele.js';

/** Écritures offertes par le contrôleur ; chacune rend vrai si le serveur a enregistré. */
export interface ActionsVue {
  marquerFait: (code: string) => Promise<boolean>;
  marquerSansObjet: (code: string, motif: string) => Promise<boolean>;
  rouvrir: (code: string) => Promise<boolean>;
  repondre: (code: string, reponse: string) => Promise<boolean>;
  forcer: (code: string, conditions: readonly string[], motif: string, leverMandat: boolean) => Promise<boolean>;
  cocher: (code: string, codeCondition: string, coche: boolean) => Promise<boolean>;
  demanderDeblocage: (code: string, conditions: readonly string[]) => Promise<boolean>;
  /** Tournage du 30/10, défaut 2 : saisir le défunt (rôle « auteur ») depuis la condition « Défunt renseigné ». */
  saisirDefunt?: (prenoms: string, nom: string) => Promise<boolean>;
}

/** Vue du démarrage guidé (L64) : prochaine action d'abord, puis la frise des phases, puis le cas particulier. */
export interface PropsVue {
  catalogue: CatalogueDemarrage;
  avancement: readonly AvancementEtape[];
  role: RoleCode;
  dossierId: string;
  actions: ActionsVue;
  /** Chargement ou écriture en cours : les boutons d'écriture sont désactivés. */
  occupe: boolean;
  /** Dernière écriture refusée, dite là où l'on a cliqué (U3). */
  erreurEcriture: { code: string; message: string } | null;
  /** Mandat de recherche d'héritiers signé au dossier (étape 21) ; null : les mandats n'ont pas pu être lus. */
  mandatRechercheSigne: boolean | null;
  mandatRechercheSigneLe: string | null;
  /** Forçages tracés du dossier (point 5). */
  forcages?: readonly ForcageLu[];
  /** Nombre de personnes de l'arbre (point 4) ; null si inconnu. */
  personnesArbre?: number | null;
  /** Nombre de personnes de l'arbre au rôle « auteur » (condition « Défunt renseigné ») ; null si inconnu. */
  defuntsArbre?: number | null;
  /** Ouvert depuis le guide (`guide=1`) : les écrans ouverts gardent le guide et l'étape dans l'adresse (point 2). */
  guide?: boolean;
}

/** Où l'on a cliqué : la carte « Prochaine action », ou la ligne de l'étape. */
type Lieu = 'carte' | 'ligne';

/** Ce que chaque morceau de la vue partage. */
interface Partage extends Omit<PropsVue, 'avancement' | 'forcages'> {
  forcages: readonly ForcageLu[];
  /** Ce que l'on sait du dossier pour évaluer les conditions d'une étape (point 4). */
  faits: (etape: EtapeCatalogue) => FaitsDossier;
  choix: (etape: EtapeCatalogue) => string | null;
  /** Réponse imposée par les pièces du dossier (étape 21 sans mandat de recherche signé). */
  imposee: (etape: EtapeCatalogue) => OptionQuestion | null;
  choisir: (etape: EtapeCatalogue, option: string, lieu: Lieu) => void;
  ecrire: (etape: EtapeCatalogue, lieu: Lieu, action: () => Promise<boolean>) => Promise<boolean>;
  faire: (etape: EtapeCatalogue, lieu: Lieu) => void;
  origine: { code: string; lieu: Lieu } | null;
  titreCarte: RefObject<HTMLHeadingElement | null>;
}

const TAILLE = 16;
const MOTIF_MAX = 200;
const carte: CSSProperties = { border: `1px solid ${COULEURS.bordure}`, borderRadius: 8, padding: 16, background: COULEURS.blanc };
const titre2: CSSProperties = { fontSize: 20, color: COULEURS.marine, margin: 0 };
const discret: CSSProperties = { color: COULEURS.ardoise, margin: 0, fontSize: TAILLE };
const boutonDepli: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  minHeight: CIBLE_MIN,
  padding: '8px 12px',
  border: 0,
  borderRadius: 8,
  background: 'transparent',
  color: COULEURS.marine,
  font: 'inherit',
  fontSize: TAILLE,
  textAlign: 'left',
  cursor: 'pointer',
  boxSizing: 'border-box',
};
const grosBouton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 48,
  padding: '0 20px',
  textDecoration: 'none',
  boxSizing: 'border-box',
  maxWidth: '100%',
  textAlign: 'center',
  fontSize: TAILLE,
};
const lienBouton: CSSProperties = { ...styleBoutonPrincipal, ...grosBouton };
const boutonFait: CSSProperties = {
  ...styleBouton,
  ...grosBouton,
  border: `2px solid ${COULEURS.marine}`,
  background: COULEURS.marine,
  color: COULEURS.blanc,
  fontWeight: 600,
};
const boutonSecondaire: CSSProperties = { ...styleBouton, ...grosBouton };
const boutonSecondaireLien: CSSProperties = { ...boutonSecondaire, display: 'inline-flex' };
const rangee: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' };
const alerte: CSSProperties = { ...carte, background: COULEURS.ambreFond, color: COULEURS.ambre, borderColor: COULEURS.ambre, margin: 0 };
const styleErreur: CSSProperties = { ...carte, borderColor: COULEURS.rouge, color: COULEURS.rouge, margin: 0 };

const FOND_BADGE: Readonly<Record<Intervenant, string>> = {
  dirigeante: COULEURS.marine,
  assistante: COULEURS.indigo,
  notaire: COULEURS.ardoise,
};

/** Intervenant du catalogue que le rôle de la session incarne (l'administration n'en incarne aucun). */
const INTERVENANT_DU_ROLE: Partial<Readonly<Record<RoleCode, Intervenant>>> = { dirigeante: 'dirigeante', assistante: 'assistante' };

/** U9 : quand un autre fait l'étape, la phrase dit qui la fait et quand la cocher. */
const phraseAutre = (etape: EtapeCatalogue, p: Partage): string | null => {
  const moi = INTERVENANT_DU_ROLE[p.role];
  return moi && etape.qui !== moi ? (p.catalogue.quandUnAutreFait[etape.qui] ?? null) : null;
};

function Badge({ catalogue, qui }: { catalogue: CatalogueDemarrage; qui: Intervenant }) {
  return (
    <span
      data-intervenant={qui}
      style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, background: FOND_BADGE[qui], color: COULEURS.blanc, fontSize: TAILLE }}
    >
      {catalogue.intervenants[qui]}
    </span>
  );
}

function LibelleStatut({ statut, avancement }: { statut: Statut; avancement: AvancementEtape | undefined }) {
  if (statut === 'fait' || statut === 'sans_objet') {
    const quand = avancement?.faitLe ? ` le ${jourMois(avancement.faitLe)}` : '';
    const qui = avancement?.faitPar ? ` par ${avancement.faitPar}` : '';
    const libelle = `${statut === 'fait' ? 'Fait' : 'Sans objet'}${quand}${qui}`;
    return <span style={{ color: COULEURS.marine, fontSize: TAILLE }}>{libelle}</span>;
  }
  return statut === 'en_cours' ? (
    <span style={{ color: COULEURS.boutonPrincipal, fontWeight: 600, fontSize: TAILLE }}>En cours</span>
  ) : (
    <span style={{ color: COULEURS.ardoise, fontSize: TAILLE }}>À venir</span>
  );
}

/** Adresse d'un écran ouvert depuis une étape : le dossier, et depuis le guide, `guide=1` et l'étape (point 2, jamais sortir du guide). */
const adresseEtape = (ecran: string, etape: EtapeCatalogue, p: Partage): string =>
  `${cheminEcran(ecran)}?dossier=${encodeURIComponent(p.dossierId)}${p.guide ? `&guide=1&etape=${etape.code}` : ''}`;

/** Bouton « Ouvrir » vers l'écran de l'étape, dossier gardé ; un écran que le rôle n'ouvre pas n'est jamais proposé. */
function OuvrirEcran({ ecran, etape, p }: { ecran: string; etape: EtapeCatalogue; p: Partage }) {
  if (!peutOuvrir(p.role, ecran)) {
    // La phrase « Isabelle fait cette étape… » dit déjà pourquoi ; sinon, on le dit sans renvoyer la tâche.
    return phraseAutre(etape, p) ? null : <p style={discret}>Cet écran est réservé à {p.catalogue.intervenants.dirigeante}.</p>;
  }
  const vers = adresseEtape(ecran, etape, p);
  // Le nom que l'équipe lit dans les onglets du dossier (« Vue d'ensemble », « Biens et titres »), sinon celui du menu.
  const libelle = ongletsDossier(p.role, p.dossierId).find((o) => o.id === ecran && o.cle === ecran)?.libelle ?? libelleNavigation(ecran);
  return <Lien vers={vers} style={lienBouton} enfants={`Ouvrir « ${libelle} »`} />;
}

function AConfirmer({ points }: { points: readonly string[] | undefined }) {
  if (!points?.length) return null;
  return (
    <div data-a-verifier="" style={{ ...discret, fontStyle: 'italic' }}>
      <p style={{ margin: 0 }}>À confirmer par Isabelle :</p>
      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
        {points.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

/** U3 : l'erreur d'une écriture est dite là où l'on a cliqué, et reçoit le focus. */
function ErreurIci({ etape, lieu, p }: { etape: EtapeCatalogue; lieu: Lieu; p: Partage }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const ici = p.erreurEcriture?.code === etape.code && p.origine?.code === etape.code && p.origine.lieu === lieu;
  useEffect(() => {
    if (ici) ref.current?.focus();
  }, [ici, p.erreurEcriture]);
  if (!ici) return null;
  return (
    <p ref={ref} role="alert" tabIndex={-1} style={styleErreur}>
      {p.erreurEcriture?.message}
    </p>
  );
}

/** Question de l'étape (12, 19, 21) : de gros choix explicites, jamais une liste déroulante ; la réponse reste modifiable. */
function Question({ etape, lieu, p }: { etape: EtapeCatalogue; lieu: Lieu; p: Partage }) {
  const question = etape.question;
  if (!question) return null;
  const choix = p.choix(etape);
  const imposee = p.imposee(etape);
  return (
    <fieldset style={{ border: `1px solid ${COULEURS.bordure}`, borderRadius: 8, padding: 12, margin: 0, minWidth: 0 }}>
      <legend style={{ fontWeight: 600, color: COULEURS.marine, padding: '0 4px' }}>{question.libelle}</legend>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {question.options.map((o) => {
          const bloquee = imposee !== null && o.code !== imposee.code;
          return (
            <label
              key={o.code}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 48,
                padding: '4px 12px',
                borderRadius: 8,
                border: `${choix === o.code ? 2 : 1}px solid ${choix === o.code ? COULEURS.marine : COULEURS.bordure}`,
                background: choix === o.code ? COULEURS.surface : COULEURS.blanc,
                color: bloquee ? COULEURS.ardoise : 'inherit',
                cursor: bloquee ? 'not-allowed' : 'pointer',
              }}
            >
              <input
                type="radio"
                name={`demarrage-${lieu}-${etape.code}`}
                value={o.code}
                checked={choix === o.code}
                disabled={p.occupe || bloquee}
                onChange={() => p.choisir(etape, o.code, lieu)}
                style={{ width: 24, height: 24, margin: 0, flex: '0 0 auto', accentColor: COULEURS.marine }}
              />
              <span>{o.libelle}</span>
            </label>
          );
        })}
      </div>
      {imposee && !imposee.rechercheInterdite ? (
        <p style={{ ...discret, marginTop: 8 }}>
          {p.mandatRechercheSigneLe ? `Mandat de recherche signé le ${jourMois(p.mandatRechercheSigneLe)}.` : 'Mandat de recherche signé au dossier.'}
        </p>
      ) : imposee ? (
        <p style={{ ...discret, marginTop: 8 }}>
          {p.mandatRechercheSigne === null
            ? 'Les mandats du dossier n’ont pas pu être lus : la réponse « Oui » n’est pas possible pour l’instant.'
            : 'Aucun mandat de recherche d’héritiers signé n’est enregistré dans Documents : la réponse « Oui » n’est pas possible.'}
        </p>
      ) : null}
      <ErreurIci etape={etape} lieu={lieu} p={p} />
    </fieldset>
  );
}

/** Consigne de la réponse choisie ; sans mandat (étape 21), un message clair et aucune action de recherche. */
function ConsigneReponse({ option }: { option: OptionQuestion | null }) {
  if (!option) return null;
  const fort = option.rechercheInterdite || option.finDeMission;
  return (
    <div data-consigne-reponse="" style={fort ? alerte : { ...carte, background: COULEURS.surface }}>
      <p style={{ margin: 0, fontWeight: fort ? 600 : 400 }}>{option.consigne}</p>
      <AConfirmer points={option.aVerifier} />
    </div>
  );
}

function QuiFait({ etape, p }: { etape: EtapeCatalogue; p: Partage }) {
  const phrase = phraseAutre(etape, p);
  return phrase ? <p data-qui-fait="" style={{ ...discret, color: COULEURS.marineFonce }}>{phrase}</p> : null;
}

const ecranDe = (etape: EtapeCatalogue, option: OptionQuestion | null): string => option?.ecran ?? etape.ecran;

/** Réouverture d'une étape faite : jamais au premier clic, toujours après confirmation. */
function Rouvrir({ etape, lieu, libelle, p }: { etape: EtapeCatalogue; lieu: Lieu; libelle: string; p: Partage }) {
  const [confirmer, setConfirmer] = useState(false);
  if (!confirmer) {
    return (
      <div style={rangee}>
        <button type="button" style={boutonSecondaire} disabled={p.occupe} onClick={() => setConfirmer(true)}>
          {libelle}
        </button>
      </div>
    );
  }
  return (
    <div role="group" aria-label="Confirmer la réouverture" style={{ ...carte, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ margin: 0 }}>Voulez-vous vraiment rouvrir cette étape ? Elle redeviendra à faire ; sa réponse est gardée.</p>
      <div style={rangee}>
        <button
          type="button"
          style={boutonFait}
          disabled={p.occupe}
          onClick={() => void p.ecrire(etape, lieu, () => p.actions.rouvrir(etape.code)).then((ok) => ok && setConfirmer(false))}
        >
          Oui, rouvrir
        </button>
        <button type="button" style={boutonSecondaire} onClick={() => setConfirmer(false)}>
          Annuler
        </button>
      </div>
    </div>
  );
}

function Ecritures({ etape, statut, p }: { etape: EtapeCatalogue; statut: Statut; p: Partage }) {
  const [sansObjet, setSansObjet] = useState(false);
  const [motif, setMotif] = useState('');
  const id = useId();
  if (statut === 'fait' || statut === 'sans_objet') return <Rouvrir etape={etape} lieu="ligne" libelle="Rouvrir l’étape" p={p} />;
  if (sansObjet) {
    const pret = motif.trim() !== '';
    return (
      <div style={{ ...carte, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label htmlFor={id} style={{ fontWeight: 600 }}>
          Pourquoi cette étape est-elle sans objet ? (une phrase)
        </label>
        <textarea
          id={id}
          value={motif}
          maxLength={MOTIF_MAX}
          rows={2}
          onChange={(e) => setMotif(e.target.value)}
          style={{ font: 'inherit', fontSize: TAILLE, padding: 8, borderRadius: 8, border: `1px solid ${COULEURS.bordure}`, width: '100%', boxSizing: 'border-box' }}
        />
        <div style={rangee}>
          <button
            type="button"
            style={boutonFait}
            disabled={!pret || p.occupe}
            onClick={() =>
              void p.ecrire(etape, 'ligne', () => p.actions.marquerSansObjet(etape.code, motif)).then((ok) => ok && setSansObjet(false))
            }
          >
            Enregistrer « Sans objet »
          </button>
          <button type="button" style={boutonSecondaire} onClick={() => setSansObjet(false)}>
            Annuler
          </button>
        </div>
      </div>
    );
  }
  return (
    <div style={rangee}>
      <BoutonFait etape={etape} lieu="ligne" p={p} />
      <button type="button" style={boutonSecondaire} disabled={p.occupe} onClick={() => setSansObjet(true)}>
        Sans objet
      </button>
    </div>
  );
}

const LIBELLE_CONDITION = { oui: 'Remplie', non: 'Manquante', forcee: 'Levée par un forçage' } as const;

/** Point 4 : les conditions de passage, évaluées sur le dossier, chacune avec le bouton qui permet de la remplir tout de suite. */
function Conditions({ etape, p }: { etape: EtapeCatalogue; p: Partage }) {
  const liste = evaluerConditions(etape, p.faits(etape));
  if (liste.length === 0) return null;
  return (
    <div data-conditions="" style={{ ...carte, background: COULEURS.surface, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p style={{ margin: 0, fontWeight: 600 }}>Pour passer à la suite :</p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {liste.map(({ condition, etat }) => (
          <li key={condition.code} data-condition={condition.code} data-remplie={etat} style={{ ...rangee, gap: 8 }}>
            <span aria-hidden="true" style={{ fontWeight: 700, color: etat === 'non' ? COULEURS.ambre : COULEURS.marine }}>
              {etat === 'non' ? '!' : '✓'}
            </span>
            {condition.evaluation === 'manuelle' ? (
              <label style={{ flex: '1 1 200px', display: 'flex', alignItems: 'center', gap: 8, minHeight: CIBLE_MIN, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={etat === 'oui'}
                  disabled={p.occupe}
                  onChange={(e) => void p.ecrire(etape, 'carte', () => p.actions.cocher(etape.code, condition.code, e.target.checked))}
                  style={{ width: 24, height: 24, margin: 0, accentColor: COULEURS.marine }}
                />
                <span>
                  {condition.libelle} : <strong>{LIBELLE_CONDITION[etat]}</strong>
                </span>
              </label>
            ) : (
              <span style={{ flex: '1 1 200px' }}>
                {condition.libelle} : <strong>{LIBELLE_CONDITION[etat]}</strong>
              </span>
            )}
            {etat === 'non' && condition.ecran && peutOuvrir(p.role, condition.ecran) ? (
              <Lien
                vers={adresseEtape(condition.ecran, etape, p)}
                style={boutonSecondaireLien}
                enfants={condition.bouton ?? `Ouvrir « ${libelleNavigation(condition.ecran)} »`}
              />
            ) : null}
            {etat === 'non' && condition.evaluation === 'defunt_renseigne' && p.actions.saisirDefunt ? <SaisieDefunt p={p} /> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Tournage du 30/10, défaut 2 : le défunt manquant se saisit sur place (prénom, nom), sans quitter le guide. Le reste de sa fiche
 * (dates, lieux, situation matrimoniale) se complète dans le tableau du défunt.
 */
function SaisieDefunt({ p }: { p: Partage }) {
  const [prenoms, setPrenoms] = useState('');
  const [nom, setNom] = useState('');
  const idPrenoms = useId();
  const idNom = useId();
  const champ: CSSProperties = { minHeight: CIBLE_MIN, font: 'inherit', padding: '0 12px', borderRadius: 12, border: `1px solid ${COULEURS.bordure}` };
  const erreur = p.erreurEcriture?.code === 'defunt' ? p.erreurEcriture.message : null;
  return (
    <div data-saisie-defunt="" style={{ flex: '1 1 100%', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label htmlFor={idPrenoms}>Prénom du défunt</label>
        <input id={idPrenoms} value={prenoms} onChange={(e) => setPrenoms(e.target.value)} style={champ} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label htmlFor={idNom}>Nom du défunt</label>
        <input id={idNom} value={nom} onChange={(e) => setNom(e.target.value)} style={champ} />
      </div>
      <button type="button" style={boutonSecondaire} disabled={p.occupe || nom.trim() === ''} onClick={() => void p.actions.saisirDefunt?.(prenoms, nom)}>
        Enregistrer le défunt
      </button>
      {erreur ? (
        <p role="alert" style={{ margin: 0, flex: '1 1 100%' }}>
          {erreur}
        </p>
      ) : null}
    </div>
  );
}

/** Point 5 : un forçage de l'étape est rappelé là où on la lit (qui, quand, motif). */
function Forcages({ etape, p }: { etape: EtapeCatalogue; p: Partage }) {
  const liste = p.forcages.filter((f) => f.portee === 'etape' && f.codeEtape === etape.code);
  if (liste.length === 0) return null;
  return (
    <>
      {liste.map((f) => (
        <p key={f.id} data-forcage="" style={{ ...discret, padding: '0 12px 8px', color: COULEURS.ambre }}>
          Forcé le {jourMois(f.forceLe)} par {f.forcePar} (manquait : {f.conditions.join(', ')}). Motif : {f.motif}
        </p>
      ))}
    </>
  );
}

function BoutonFait({ etape, lieu, p }: { etape: EtapeCatalogue; lieu: Lieu; p: Partage }) {
  const choix = p.choix(etape);
  const raison = etape.question && !choix ? 'Répondez d’abord à la question.' : null;
  return (
    <>
      <button type="button" style={boutonFait} disabled={p.occupe || raison !== null} onClick={() => p.faire(etape, lieu)}>
        C’est fait
      </button>
      {raison ? <span style={discret}>{raison}</span> : null}
    </>
  );
}

function Detail({ etape, statut, p }: { etape: EtapeCatalogue; statut: Statut; p: Partage }) {
  const option = optionChoisie(etape, p.choix(etape));
  return (
    <div data-detail="" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 12px 16px' }}>
      <p style={{ margin: 0 }}>
        <strong>Ce qu’il faut faire : </strong>
        {etape.quoiFaire}
      </p>
      {etape.details?.length ? (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {etape.details.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      ) : null}
      <p style={{ margin: 0 }}>
        <strong>Qui : </strong>
        {p.catalogue.intervenants[etape.qui]}
        {etape.suiviPar ? `, suivi par ${p.catalogue.intervenants[etape.suiviPar]}` : ''}
      </p>
      <QuiFait etape={etape} p={p} />
      {etape.montants?.length ? (
        <ul aria-label="Montants" style={{ margin: 0, paddingLeft: 20 }}>
          {etape.montants.map((m) => (
            <li key={m.libelle}>
              {m.libelle} : {euros(m.euros)}
            </li>
          ))}
        </ul>
      ) : null}
      {etape.mentionEstimation ? (
        <p data-mention-estimation="" style={alerte}>
          {MENTION_ESTIMATION}
        </p>
      ) : null}
      <AConfirmer points={etape.aVerifier} />
      <Conditions etape={etape} p={p} />
      <Question etape={etape} lieu="ligne" p={p} />
      <ConsigneReponse option={option} />
      <div>
        <OuvrirEcran ecran={ecranDe(etape, option)} etape={etape} p={p} />
      </div>
      <ErreurIci etape={etape} lieu="ligne" p={p} />
      <Ecritures etape={etape} statut={statut} p={p} />
    </div>
  );
}

function LigneEtape({
  etape,
  statut,
  avancement,
  ouverte,
  onBasculer,
  p,
}: {
  etape: EtapeCatalogue;
  statut: Statut;
  avancement: AvancementEtape | undefined;
  ouverte: boolean;
  onBasculer: () => void;
  p: Partage;
}) {
  const idDetail = `demarrage-${etape.code}`;
  return (
    <li
      data-etape={etape.code}
      {...(statut === 'en_cours' ? { 'aria-current': 'step' as const } : {})}
      style={{
        borderLeft: `4px solid ${statut === 'en_cours' ? COULEURS.boutonPrincipal : 'transparent'}`,
        background: statut === 'en_cours' ? COULEURS.surface : 'transparent',
        borderRadius: 4,
      }}
    >
      <button type="button" aria-expanded={ouverte} aria-controls={idDetail} onClick={onBasculer} style={boutonDepli}>
        <span style={{ flex: '1 1 220px', fontWeight: 600 }}>
          {etape.numero}. {etape.titre}
        </span>
        <Badge catalogue={p.catalogue} qui={etape.qui} />
        <LibelleStatut statut={statut} avancement={avancement} />
      </button>
      {statut === 'sans_objet' && avancement?.motif ? (
        <p data-motif="" style={{ ...discret, padding: '0 12px 8px' }}>
          Motif : {avancement.motif}
        </p>
      ) : null}
      <Forcages etape={etape} p={p} />
      <div id={idDetail} hidden={!ouverte}>
        {ouverte ? <Detail etape={etape} statut={statut} p={p} /> : null}
      </div>
    </li>
  );
}

function ProchaineAction({
  etape,
  finDeMission,
  p,
}: {
  etape: EtapeCatalogue | null;
  finDeMission: { etape: EtapeCatalogue; option: OptionQuestion } | null;
  p: Partage;
}) {
  const option = etape ? optionChoisie(etape, p.choix(etape)) : null;
  return (
    <section
      data-prochaine-action=""
      aria-labelledby="demarrage-prochaine"
      style={{ ...carte, borderColor: COULEURS.marine, borderWidth: 2, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <h2 id="demarrage-prochaine" ref={p.titreCarte} tabIndex={-1} style={titre2}>
        Prochaine action
      </h2>
      {finDeMission ? (
        <>
          <p style={discret}>
            Étape {finDeMission.etape.numero} · {finDeMission.etape.titre}
          </p>
          <p style={{ ...alerte, fontWeight: 600 }}>{finDeMission.option.consigne}</p>
          <ErreurIci etape={finDeMission.etape} lieu="carte" p={p} />
          <Rouvrir etape={finDeMission.etape} lieu="carte" libelle={`Rouvrir l’étape ${finDeMission.etape.numero}`} p={p} />
        </>
      ) : etape ? (
        <>
          <p style={discret}>
            Étape {etape.numero} sur {p.catalogue.etapes.length}
          </p>
          <p style={{ margin: 0, fontSize: 22, fontWeight: 600, color: COULEURS.marineFonce }}>{etape.titre}</p>
          <p style={{ margin: 0 }}>
            <Badge catalogue={p.catalogue} qui={etape.qui} />
          </p>
          <p style={{ margin: 0 }}>{etape.quoiFaire}</p>
          <QuiFait etape={etape} p={p} />
          <Conditions etape={etape} p={p} />
          <Question etape={etape} lieu="carte" p={p} />
          <ConsigneReponse option={option} />
          <ErreurIci etape={etape} lieu="carte" p={p} />
          <div style={rangee}>
            <OuvrirEcran ecran={ecranDe(etape, option)} etape={etape} p={p} />
            <BoutonFait etape={etape} lieu="carte" p={p} />
          </div>
        </>
      ) : (
        <p style={{ margin: 0 }}>Toutes les étapes du parcours sont faites.</p>
      )}
    </section>
  );
}

/** Largeur de la fenêtre, suivie au redimensionnement (comme la vue d'ensemble du dossier). */
function useLargeur(): number {
  const lire = () => globalThis.innerWidth ?? 1280;
  const [largeur, setLargeur] = useState(lire);
  useEffect(() => {
    const mesurer = () => setLargeur(lire());
    globalThis.addEventListener?.('resize', mesurer);
    return () => globalThis.removeEventListener?.('resize', mesurer);
  }, []);
  return largeur;
}

const LIBELLE_PHASE = (ph: ResumePhase): string => (ph.terminee ? 'Terminée' : ph.enCours ? 'En cours' : 'À venir');

/**
 * U6 (juge du 30/10) : frise compacte des cinq phases, sur le modèle du « Parcours du dossier » de la vue d'ensemble : une rangée de
 * cinq cases sur bureau, une seule colonne sur mobile ; la phase en cours est marquée.
 */
function Frise({ phases }: { phases: readonly ResumePhase[] }) {
  const mobile = useLargeur() < LARGEUR_BUREAU;
  return (
    <ol
      data-frise=""
      aria-label="Phases du dossier"
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        gap: 8,
        // N2 : sur mobile, une ligne compacte qui défile (au plus 200 px de haut), jamais une case par ligne.
        ...(mobile
          ? { display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', maxHeight: 200 }
          : { display: 'grid', gridTemplateColumns: `repeat(${phases.length}, minmax(0, 1fr))` }),
      }}
    >
      {phases.map((ph) => {
        const couleur = ph.terminee ? COULEURS.marine : ph.enCours ? COULEURS.boutonPrincipal : COULEURS.bordure;
        return (
          <li
            key={ph.code}
            data-frise-phase={ph.code}
            {...(ph.enCours ? { 'aria-current': 'true' as const } : {})}
            style={{
              ...carte,
              padding: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              flexShrink: 0,
              borderTop: `6px solid ${couleur}`,
              background: ph.enCours ? COULEURS.surface : COULEURS.blanc,
            }}
          >
            {/* Espaces entre les morceaux : un lecteur d'écran lit « Consultation 5 sur 5 Terminée », pas un seul mot. */}
            <span style={{ fontWeight: 600, color: COULEURS.marine, whiteSpace: 'nowrap' }}>{ph.libelle}</span>{' '}
            <span style={{ whiteSpace: 'nowrap', color: ph.enCours ? COULEURS.boutonPrincipal : COULEURS.ardoise, fontWeight: ph.enCours ? 600 : 400 }}>
              {ph.terminees} sur {ph.etapes.length} · {LIBELLE_PHASE(ph)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Avancement({ terminees, total }: { terminees: number; total: number }) {
  const pourcent = total ? Math.round((terminees / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <p style={{ margin: 0, fontWeight: 600 }}>
        {etapes(terminees)} sur {total}
      </p>
      <div
        role="progressbar"
        aria-label="Avancement du dossier"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={terminees}
        aria-valuetext={`${etapes(terminees)} sur ${total}`}
        style={{ height: 12, borderRadius: 6, background: COULEURS.surface, border: `1px solid ${COULEURS.bordure}`, overflow: 'hidden' }}
      >
        <div style={{ width: `${pourcent}%`, height: '100%', background: COULEURS.marine }} />
      </div>
    </div>
  );
}

export function VueDemarrage(props: PropsVue) {
  const { catalogue, avancement, forcages = [], personnesArbre = null, defuntsArbre = null, ...reste } = props;
  const [avertissement, setAvertissement] = useState<{ etape: EtapeCatalogue; lieu: Lieu; conditions: string[]; leverMandat: boolean } | null>(null);
  const resume = resumer(catalogue, avancement);
  const lus = parCode(avancement);
  const [phasesOuvertes, setPhasesOuvertes] = useState<Readonly<Record<string, boolean>>>({});
  const [etapeOuverte, setEtapeOuverte] = useState<string | null>(null);
  // Réponse choisie à l'instant, montrée aussitôt ; elle cède la place à celle du serveur, ou est retirée si l'écriture échoue.
  const [choixLocaux, setChoixLocaux] = useState<Readonly<Record<string, string>>>({});
  const [origine, setOrigine] = useState<Partage['origine']>(null);
  const titreCarte = useRef<HTMLHeadingElement>(null);
  const ouvrirLaSuivante = useRef(false);
  const codeCourant = resume.courante?.code ?? null;
  useEffect(() => {
    if (!ouvrirLaSuivante.current) return;
    ouvrirLaSuivante.current = false;
    setEtapeOuverte((o) => (o === null ? null : codeCourant));
  }, [codeCourant]);

  // Étape 21 : la réponse suit les mandats réellement au dossier. Sans mandat de recherche signé, « Non » ; avec, « Oui » (N1).
  const imposee = (e: EtapeCatalogue): OptionQuestion | null => {
    const options = e.question?.options ?? [];
    if (!options.some((o) => o.rechercheInterdite)) return null;
    return reste.mandatRechercheSigne === true ? (options.find((o) => !o.rechercheInterdite) ?? null) : (options.find((o) => o.rechercheInterdite) ?? null);
  };
  const ecrire: Partage['ecrire'] = async (e, lieu, action) => {
    setOrigine({ code: e.code, lieu });
    const ok = await action();
    // U5 : après un enregistrement réussi, le focus revient à la prochaine action.
    if (ok) titreCarte.current?.focus();
    return ok;
  };
  const faitsDe = (e: EtapeCatalogue): FaitsDossier => ({
    mandatRechercheSigne: reste.mandatRechercheSigne,
    forcages,
    personnesArbre,
    defuntsArbre,
    cochees: lus.get(e.code)?.conditionsCochees ?? [],
  });
  const p: Partage = {
    ...reste,
    forcages,
    faits: faitsDe,
    catalogue,
    origine,
    titreCarte,
    imposee,
    choix: (e) => imposee(e)?.code ?? choixLocaux[e.code] ?? lus.get(e.code)?.reponse ?? null,
    choisir: (e, option, lieu) => {
      setOrigine({ code: e.code, lieu });
      setChoixLocaux((c) => ({ ...c, [e.code]: option }));
      void reste.actions.repondre(e.code, option).then(() =>
        setChoixLocaux((c) => Object.fromEntries(Object.entries(c).filter(([code]) => code !== e.code))),
      );
    },
    ecrire,
    faire: (e, lieu) => {
      // Point 5 : jamais bloqué. Ce qui manque ouvre l'avertissement ; rien n'est écrit avant la confirmation.
      const option = optionChoisie(e, p.choix(e));
      const faits = faitsDe(e);
      const manque = manquantes(e, option, faits);
      if (manque.length > 0) {
        const leverMandat =
          manque.includes(CONDITION_MANDAT_RECHERCHE) ||
          evaluerConditions(e, faits).some((c) => c.etat === 'non' && c.condition.evaluation === 'mandat_recherche_signe');
        setAvertissement({ etape: e, lieu, conditions: manque, leverMandat });
        return;
      }
      ouvrirLaSuivante.current = true;
      void ecrire(e, lieu, () => reste.actions.marquerFait(e.code));
    },
  };
  // Les phases terminées sont repliées ; la phase en cours et les suivantes restent dépliées : les étapes restantes se lisent d'un coup.
  const phaseOuverte = (code: string, terminee: boolean) => phasesOuvertes[code] ?? !terminee;
  const ligne = (e: EtapeCatalogue): ReactNode => (
    <LigneEtape
      key={e.code}
      etape={e}
      statut={resume.statuts[e.code] ?? 'a_venir'}
      avancement={lus.get(e.code)}
      ouverte={etapeOuverte === e.code}
      onBasculer={() => setEtapeOuverte((o) => (o === e.code ? null : e.code))}
      p={p}
    />
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
      <ProchaineAction etape={resume.courante} finDeMission={resume.finDeMission} p={p} />
      {/* U7 : sous la carte, pour que ses boutons restent visibles au chargement sur mobile. */}
      {resume.terminees === 0 ? (
        <p data-accueil="" style={{ margin: 0 }}>
          Suivez les étapes dans l’ordre : la prochaine action est toujours affichée en haut de cette page.
        </p>
      ) : null}
      <Frise phases={resume.phases} />
      <Avancement terminees={resume.terminees} total={resume.total} />
      <section aria-labelledby="demarrage-phases" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h2 id="demarrage-phases" style={titre2}>
          Les étapes, phase par phase
        </h2>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {resume.phases.map((ph, i) => {
            const ouverte = phaseOuverte(ph.code, ph.terminee);
            const idListe = `demarrage-phase-${ph.code}`;
            return (
              <li key={ph.code} data-phase={ph.code} style={{ ...carte, padding: 4 }}>
                <button
                  type="button"
                  aria-expanded={ouverte}
                  aria-controls={idListe}
                  onClick={() => setPhasesOuvertes((o) => ({ ...o, [ph.code]: !ouverte }))}
                  style={{ ...boutonDepli, fontWeight: 600, fontSize: 18 }}
                >
                  <span style={{ flex: '1 1 200px' }}>
                    {i + 1}. {ph.libelle}
                  </span>
                  <span style={{ fontWeight: 400 }}>
                    {ph.terminees} sur {ph.etapes.length}
                    {ph.terminee ? ' · terminée' : ph.enCours ? ' · en cours' : ''}
                  </span>
                </button>
                <ol id={idListe} hidden={!ouverte} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {ouverte ? ph.etapes.map(ligne) : null}
                </ol>
              </li>
            );
          })}
        </ol>
      </section>
      <section data-cas-particulier="" aria-labelledby="demarrage-cas" style={{ ...carte, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h2 id="demarrage-cas" style={titre2}>
          {catalogue.casParticulier.libelle}
        </h2>
        <p style={discret}>À ouvrir seulement si un héritier ne peut pas être joint.</p>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>{resume.casParticulier.map(ligne)}</ol>
      </section>
      {avertissement ? (
        <AvertissementForcage
          conditions={avertissement.conditions}
          role={reste.role}
          dirigeante={catalogue.intervenants.dirigeante}
          annuler={() => setAvertissement(null)}
          forcer={async (motif) => {
            const a = avertissement;
            ouvrirLaSuivante.current = true;
            const ok = await ecrire(a.etape, a.lieu, () => reste.actions.forcer(a.etape.code, a.conditions, motif, a.leverMandat));
            if (ok) setAvertissement(null);
            else ouvrirLaSuivante.current = false;
            return ok;
          }}
          prevenir={() => reste.actions.demanderDeblocage(avertissement.etape.code, avertissement.conditions)}
          remplir={<Conditions etape={avertissement.etape} p={p} />}
        />
      ) : null}
      <p style={discret}>
        Parcours rédigé d’après les consignes d’Isabelle, version du {dateDeVersion(catalogue.version)}, {catalogue.statut}.
      </p>
    </div>
  );
}

/** Cadre commun à tous les états de l'écran (titre, police de l'équipe, taille de texte du public de 50 ans et plus). */
export function Cadre({ id, titre, children }: { id: string; titre: string; children: ReactNode }) {
  return (
    <section
      data-ecran={id}
      data-lot="L64"
      style={{
        fontFamily: POLICE_EQUIPE,
        fontSize: TAILLE,
        lineHeight: 1.5,
        color: COULEURS.marineFonce,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxWidth: 960,
        minWidth: 0,
        overflowWrap: 'anywhere',
      }}
    >
      <style>{'[data-lot="L64"] input:focus-visible,[data-lot="L64"] textarea:focus-visible{outline:3px solid #34479A;outline-offset:2px}'}</style>
      <h1 style={{ color: COULEURS.marine, fontSize: 28, margin: 0 }}>{titre}</h1>
      {children}
    </section>
  );
}

/** En-tête du dossier ouvert (U2) et place de ce guide par rapport au parcours de la vue d'ensemble. */
export function EnteteDossier({ dossier }: { dossier: { titre: string; reference: string } | null }) {
  return (
    <>
      {dossier ? (
        <p data-dossier="" style={{ margin: 0, fontSize: 18, fontWeight: 600, color: COULEURS.marine }}>
          {dossier.titre} · {dossier.reference}
        </p>
      ) : null}
      <p style={discret}>
        Ce guide suit le travail du Cabinet étape par étape ; il est indépendant du parcours de procédure affiché dans la vue d’ensemble.
      </p>
    </>
  );
}
